import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MyHeader from './header';
import Products from './products';
import ProductsHome from './productshome';

import ResponsiveMenu from './ResponsiveMenu';
import ProductModal from './ProductModal';
import axios from 'axios';

const Home3 = ({ cartItems, onAddToCart, cartList, cartTotal, showCart, onCartToggle, onRemoveItem }) => {
    // Color constants
    const ORANGE_COLOR = 'rgb(80, 80, 80)';
    
    const navigate = useNavigate();
    const [datahome, setDatahome] = React.useState([]);

    // Featured section: all items, lowest item_id first, loaded PAGE_SIZE at a time while scrolling
    const PAGE_SIZE = 25;
    const [featuredItems, setFeaturedItems] = React.useState([]);
    const [hasMore, setHasMore] = React.useState(true);
    const offsetRef = React.useRef(0);
    const loadingMoreRef = React.useRef(false);
    const hasMoreRef = React.useRef(true);
    const sentinelRef = React.useRef(null);
    const [banners, setBanners] = React.useState([]);

    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [selectedProduct, setSelectedProduct] = React.useState(null);
    const [modalLoading, setModalLoading] = React.useState(false);

    // Local state for cartList to sync with props
    const [localCartList, setLocalCartList] = React.useState(cartList);

    useEffect(() => {
        setLocalCartList(cartList);
    }, [cartList]);

    // Ensure localCartList is updated after loading finishes
    useEffect(() => {
        if (!loading) {
            setLocalCartList(cartList);
        }
    }, [loading, cartList]);

    // New state for banner slider
    const [currentSlide, setCurrentSlide] = React.useState(0);

    // State for featured categories auto-slide
    const [categoryScrollPosition, setCategoryScrollPosition] = React.useState(0);
    const categoryContainerRef = React.useRef(null);

    // Array of famous electronic brand icons (assuming icons are in public/img/brands/)
    const brandIcons = [
        process.env.PUBLIC_URL + '/img/brands/apple.png',
        process.env.PUBLIC_URL + '/img/brands/samsung.png',
        process.env.PUBLIC_URL + '/img/brands/sony.png',
        process.env.PUBLIC_URL + '/img/brands/lg.png',
        process.env.PUBLIC_URL + '/img/brands/dell.png',
        process.env.PUBLIC_URL + '/img/brands/hp.png',
        process.env.PUBLIC_URL + '/img/brands/lenovo.png'
    ];

    // Categories array
    const categories = [
        { name: 'grip series', image: process.env.PUBLIC_URL + '/img/logo1.png' },
         { name: 'grip accessories', image: process.env.PUBLIC_URL + '/img/logo1.png' },
        
        { name: 'eko series', image: process.env.PUBLIC_URL + '/img/logo1.png' },
        { name: 'x series', image: process.env.PUBLIC_URL + '/img/logo1.png' },
        { name: 'toolcases series', image: process.env.PUBLIC_URL + '/img/logo1.png' },
        { name: 'first aid series', image: process.env.PUBLIC_URL + '/img/logo1.png' },
        
    ];




    const loadMoreItems = React.useCallback(async () => {
        if (loadingMoreRef.current || !hasMoreRef.current) return;
        loadingMoreRef.current = true;
        try {
            const response = await axios.get(`/items?limit=${PAGE_SIZE}&offset=${offsetRef.current}`);
            const page = Array.isArray(response.data) ? response.data : [];
            offsetRef.current += page.length;
            setFeaturedItems((prev) => [...prev, ...page]);
            if (page.length < PAGE_SIZE) {
                hasMoreRef.current = false;
                setHasMore(false);
            }
        } catch (err) {
            console.error('Error fetching featured items:', err);
        } finally {
            loadingMoreRef.current = false;
        }
    }, []);

    // Load the next page whenever the sentinel below the list comes near the viewport.
    // Re-created after each page so a sentinel that is still visible triggers another load.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadMoreItems();
            },
            { rootMargin: '400px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, featuredItems.length, loadMoreItems]);

    useEffect(() => {
        const fetchProductshome = async () => {
            try {
                const response = await axios.get('/itemshome?featured=true&limit=10');
                setDatahome(response.data);
            } catch (err) {
                setError(err.message);
                console.error('Error fetching products:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProductshome();
    }, []);

    useEffect(() => {
        const fetchBanners = async () => {
            try {
                const response = await axios.get('/banners');
                setBanners(response.data);
            } catch (err) {
                console.error('Error fetching banners:', err);
            }
        };
        fetchBanners();
    }, []);

    // Effect for auto sliding banner every 4 seconds
    useEffect(() => {
        if (banners.length > 0) {
            const interval = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % banners.length);
            }, 4000);
            return () => clearInterval(interval);
        }
    }, [banners.length]);

    // Effect for auto-sliding featured categories every 3 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            if (categoryContainerRef.current) {
                const container = categoryContainerRef.current;
                const cardWidth = 200; // 180px width + 20px gap
                const maxScroll = container.scrollWidth - container.clientWidth;
                
                if (categoryScrollPosition >= maxScroll) {
                    // Reset to beginning
                    container.scrollTo({ left: 0, behavior: 'smooth' });
                    setCategoryScrollPosition(0);
                } else {
                    // Scroll to next position
                    const newPosition = categoryScrollPosition + cardWidth;
                    container.scrollTo({ left: newPosition, behavior: 'smooth' });
                    setCategoryScrollPosition(newPosition);
                }
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [categoryScrollPosition]);

    const handleAddToCart = (product) => {
        onAddToCart(product);
    };

    const handleItemClick = async (product) => {
        setModalLoading(true);
        try {
            const response = await axios.get(`/albums/${product.item_id}`);
            const photos = response.data.length > 0
                ? response.data.map(album => album.photo_url)
                : [product.photo_url, product.photo_url, product.photo_url]; // fallback

            setSelectedProduct({ ...product, photos });
        } catch (error) {
            console.error('Error fetching album photos:', error);
            const photos = product.photos && product.photos.length > 0
                ? product.photos
                : [product.photo_url, product.photo_url, product.photo_url]; // fallback

            setSelectedProduct({ ...product, photos });
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setSelectedProduct(null);
    };

    const handleBannerClick = (banner) => {
        if (banner.reference_link) {
            // Open the banner's reference_link page on click
            window.open(banner.reference_link, '_blank');
        } else if (banner.reference_key) {
            // Fallback: navigate to search page with reference_key query
            navigate(`/search?query=${encodeURIComponent(banner.reference_key)}`);
        } else if (banner.link) {
            // If banner has a direct link, navigate to it
            window.location.href = banner.link;
        }
    };

    return (
        <div className="modern-home">
            <MyHeader 
                cartItems={cartItems}
                cartList={cartList}
                cartTotal={cartTotal}
                showCartDropdown={showCart}
                onCartToggle={onCartToggle}
                onRemoveItem={onRemoveItem}
            />

            
<br/><br/><br/><br/>

            {/* Dynamic Banner Slider - Freshcart Style */}
            {banners.length > 0 && (
                <div className="hero-banner-container">

                    <div className="banner-swiper">
                        <div className="banner-slide">
                            <img
                                src={banners[currentSlide].banner_url}
                                alt=""
                                className="banner-image"
                            />
                        </div>
                        <div className="swiper-pagination-dots">
                            {banners.map((_, index) => (
                                <div 
                                    key={index}
                                    className={`pagination-dot ${index === currentSlide ? 'active' : ''}`}
                                    onClick={() => setCurrentSlide(index)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}

         

            

            {/* Hidden: "Panaromax Cases - Discover our latest Products" slider
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                    <div className="spinner" style={{
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #000',
                        borderRadius: '50%',
                        width: '30px',
                        height: '30px',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            ) : error ? (
                <div>Error loading productshome: {error}</div>
                ) : (
                <>
                    <div className="products-home-section">
                        <ProductsHome
                            products={datahome}
                            onAddToCart={handleAddToCart}
                            onItemClick={handleItemClick}
                            cartList={cartList}
                        />
                    </div>
                </>
            )}
            */}




 

<div className="our-products-frame">
                <div className="featured-categories-section">
                        <h2>Our Products</h2>
                </div>

                <div className="products-section">
                    <Products
                        className="home-products-grid"
                        products={featuredItems}
                        onAddToCart={handleAddToCart}
                        onItemClick={handleItemClick}
                        cartList={cartList}
                    />
                    {hasMore && <div ref={sentinelRef} style={{ height: '1px' }} />}
                </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                {/* Hidden: "View All" button
                <button
                    onClick={() => navigate('/allproducts')}
                    style={{
                        padding: '10px 20px',
                        fontSize: '16px',
                        backgroundColor: ORANGE_COLOR,
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                    }}
                >
                    View All
                </button>
                */}

                <br/>  <br/>  <br/>
            </div>

            <ProductModal 
                product={selectedProduct}
                onClose={closeModal}
                onAddToCart={handleAddToCart}
            />

            {/* Support Button */}
            <a
                href="//api.whatsapp.com/send?phone=96170412000&text=Hello,Panaromax  Sales"  target="_blank"
                className="whatsapp-support-btn"

                style={{
                    position: 'fixed',
                    bottom: '100px',
                    right: '20px',
                    zIndex: 1000,
                    backgroundColor: '#16cd12ff',
                    color: 'white',
                    borderRadius: '50%',
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '24px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease-in-out',
                    textDecoration: 'none',
                }}
                onMouseOver={(e) => {
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.3)';
                }}
                onMouseOut={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                }}
            >
                <i className="fa fa-whatsapp"></i>
            </a>

            <br/>            <br/>
            <br/>
            <br/>


            {/* Horizontal series of brand icons */}                    
            
            <style jsx>{`
                .modern-home {
                    padding-top: 70px;
                    background: #ffffff;
                    min-height: 100vh;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }

                /* Freshcart-style Banner */
                .hero-banner-container {
                    margin-top: 100px;
                    padding: 0 20px;
                    max-width: 1400px;
                    margin-left: auto;
                    margin-right: auto;
                    background: #ffffff;
                }

                .banner-swiper {
                    position: relative;
                    width: 100%;
                    aspect-ratio: 21 / 8;
                    max-height: 450px;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    background: #ffffff;
                }

                .banner-slide {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                }

                .banner-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    object-position: center;
                    display: block;
                }

                .banner-content-wrapper {
                    padding: 48px;
                    max-width: 40%;
                    z-index: 2;
                    position: relative;
                }

                .banner-badge {
                    display: inline-block;
                    padding: 8px 12px;
                    font-size: 14px;
                    font-weight: 600;
                    background-color: #fbbf24;
                    color: #1f2937;
                    border-radius: 8px;
                    margin-bottom: 28px;
                    line-height: 1;
                }

                .banner-text-content {
                    margin: 28px 0;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .banner-title {
                    font-size: 3rem;
                    font-weight: 700;
                    color: #1f2937;
                    line-height: 1.2;
                    margin: 0;
                }

                .banner-description {
                    font-size: 1rem;
                    font-weight: 300;
                    color: #4b5563;
                    margin: 0;
                    line-height: 1.5;
                }

                .banner-shop-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background-color: #1f2937;
                    color: white;
                    border: 1px solid #1f2937;
                    padding: 12px 24px;
                    border-radius: 6px;
                    font-size: 16px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .banner-shop-button:hover {
                    background-color: #111827;
                    border-color: #111827;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }

                .banner-shop-button:focus {
                    outline: none;
                    box-shadow: 0 0 0 4px rgba(55, 65, 81, 0.3);
                }

                .swiper-pagination-dots {
                    position: absolute;
                    bottom: 56px;
                    left: 0;
                    right: 0;
                    display: flex;
                    justify-content: center;
                    gap: 12px;
                    z-index: 10;
                }

                .pagination-dot {
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    background-color: rgba(255, 255, 255, 0.5);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border: 2px solid transparent;
                }

                .pagination-dot:hover {
                    background-color: rgba(255, 255, 255, 0.8);
                    transform: scale(1.2);
                }

                .pagination-dot.active {
                    background-color: white;
                    transform: scale(1.3);
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(40px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .brand-icons-container {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
                    gap: 40px;
                    padding: 10px 40px;
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.08);
                    margin: 10px auto;
                    max-width: 1200px;
                    position: relative;
                    overflow: hidden;
                }

                .brand-icons-container::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #D10024, #E91E63, #9C27B0, #673AB7);
                }

                .brand-icon {
                    width: 90%;
                    height: auto;
                    object-fit: contain;
                    cursor: pointer;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    filter: grayscale(100%) opacity(0.7);
                    margin: 0 auto;
                    display: block;
                }

                .brand-icon:hover {
                    transform: scale(1.15) translateY(-5px);
                    filter: grayscale(0%) opacity(1);
                    box-shadow: 0 10px 25px rgba(0,0,0,0.15);
                }

                .category-cards-section {
                    padding: 10px 20px;
                    background: #ffffff;
                    margin: 0;
                    position: relative;
                }

                .category-cards-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="0.5" fill="%23000" opacity="0.02"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
                    pointer-events: none;
                }

                .section-title {
                    text-align: center;
                    font-size: 3rem;
                    font-weight: 800;
                    color: #2B2D42;
                    margin-bottom: 60px;
                    position: relative;
                    letter-spacing: -1px;
                }

                .section-title::after {
                    content: '';
                    position: absolute;
                    bottom: -15px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 100px;
                    height: 4px;
                    background: linear-gradient(135deg, #D10024 0%, #E91E63 100%);
                    border-radius: 2px;
                }

                .category-cards-container {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 40px;
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 20px;
                }

                .category-card {
                    background: white;
                    border-radius: 20px;
                    width: 200px;
                    height: 200px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    position: relative;
                    overflow: hidden;
                    margin: 0 auto;
                    border: 1px solid rgba(255,255,255,0.2);
                }

                .category-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(135deg, var(--category-color), rgba(255,255,255,0.1));
                    opacity: 0;
                    transition: opacity 0.4s ease;
                    border-radius: 20px;
                }

                .category-card::after {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent);
                    transform: rotate(45deg);
                    transition: all 0.6s ease;
                    opacity: 0;
                }

                .category-card:hover::before {
                    opacity: 0.05;
                }

                .category-card:hover::after {
                    opacity: 1;
                    transform: rotate(45deg) translate(50%, 50%);
                }

                .category-card:hover {
                    transform: translateY(-15px) scale(1.05);
                    box-shadow: 0 25px 50px rgba(0,0,0,0.2);
                    border-color: var(--category-color);
                }

                .category-icon {
                    font-size: 3.5rem;
                    color: var(--category-color);
                    margin-bottom: 20px;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    z-index: 2;
                    position: relative;
                }

                .category-card:hover .category-icon {
                    transform: scale(1.2) rotate(5deg);
                    color: #2B2D42;
                    text-shadow: 0 5px 15px rgba(0,0,0,0.2);
                }

                .category-card h3 {
                    font-size: 1.2rem;
                    font-weight: 700;
                    color: #2B2D42;
                    margin: 0;
                    text-align: center;
                    transition: all 0.4s ease;
                    z-index: 2;
                    position: relative;
                    letter-spacing: 0.5px;
                }

                .category-card:hover h3 {
                    color: var(--category-color);
                    font-weight: 800;
                    transform: translateY(-2px);
                }

                .category-image-circle {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    overflow: hidden;
                    padding: 8px;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 20px;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    transition: all 0.4s ease;
                }

                .category-image-circle img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: 50%;
                }

                .category-card:hover .category-image-circle {
                    transform: scale(1.1);
                    box-shadow: 0 6px 12px rgba(0,0,0,0.2);
                }

                @media (max-width: 768px) {
                    .hero-banner-container {
                        margin-top: 80px;
                        padding: 0;
                    }

                    .banner-swiper {
                        border-radius: 0;
                    }

                    .banner-content-wrapper {
                        padding: 32px 24px;
                        max-width: 60%;
                    }

                    .banner-badge {
                        font-size: 12px;
                        padding: 6px 10px;
                        margin-bottom: 20px;
                    }

                    .banner-title {
                        font-size: 2rem;
                    }

                    .banner-description {
                        font-size: 0.9rem;
                    }

                    .banner-shop-button {
                        padding: 10px 20px;
                        font-size: 14px;
                    }

                    .swiper-pagination-dots {
                        bottom: 40px;
                    }

                    .brand-icons-container {
                        grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
                        gap: 25px;
                        padding: 40px 20px;
                    }

                    .category-cards-container {
                        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
                        gap: 25px;
                    }

                    .category-card {
                        width: 160px;
                        height: 160px;
                    }

                    .category-icon {
                        font-size: 2.8rem;
                        margin-bottom: 15px;
                    }

                    .category-card h3 {
                        font-size: 1rem;
                    }

                    .section-title {
                        font-size: 2.2rem;
                        margin-bottom: 40px;
                    }
                }

                @media (max-width: 480px) {
                    .banner-content-wrapper {
                        padding: 24px 16px;
                        max-width: 80%;
                    }

                    .banner-title {
                        font-size: 1.5rem;
                    }

                    .banner-description {
                        font-size: 0.85rem;
                    }

                    .banner-shop-button {
                        padding: 8px 16px;
                        font-size: 13px;
                    }

                    .category-cards-container {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 15px;
                    }

                    .category-card {
                        width: 100px;
                        height: 100px;
                    }

                    .category-image-circle {
                        width: 50px;
                        height: 50px;
                    }

                    .category-card h3 {
                        font-size: 0.8rem;
                    }
                }

                /* "Our Products" block (no red frame, only the red title band) */
                .our-products-frame {
                    max-width: 1400px;
                    margin: 20px auto 0;
                    border-radius: 14px;
                    background: #ffffff;
                    overflow: hidden;
                }

                /* Title band, same red gradient as the header top bar */
                .our-products-frame .featured-categories-section {
                    background: linear-gradient(135deg, #c51a1a 0%, #c51a1a 100%);
                    padding: 12px 20px;
                }

                .our-products-frame .featured-categories-section h2 {
                    color: #ffffff;
                    text-align: center;
                    font-size: 1.4rem;
                    font-weight: 700;
                }

                .our-products-frame .products-section {
                    padding-top: 8px;
                }

                /* Medium and big screens: cards start from the left */
                @media (min-width: 769px) {
                    .home-products-grid {
                        justify-content: flex-start;
                    }
                }

                @media (max-width: 768px) {
                    /* WhatsApp button only on medium and big screens (overrides inline display: flex) */
                    .whatsapp-support-btn {
                        display: none !important;
                    }

                    .our-products-frame {
                        margin: 15px 10px 0;
                        border-radius: 10px;
                    }
                }

                /* Featured Categories Section */
                .featured-categories-section {
                    padding: 30px 20px 0;
                    background: white;
                    margin: 0 auto;
                    max-width: 1400px;
                    position: relative;
                }

                .featured-categories-section h2 {
                    margin: 0;
                }

                .featured-categories-slider {
                    position: relative;
                    overflow: hidden;
                    padding: 20px 0;
                }

                .featured-categories-container {
                    display: flex;
                    gap: 20px;
                    overflow-x: auto;
                    scroll-behavior: smooth;
                    padding: 10px 0;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }

                .featured-categories-container::-webkit-scrollbar {
                    display: none;
                }

                .featured-category-card {
                    flex: 0 0 auto;
                    width: 180px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .featured-category-content {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 10px 7px;
                    text-align: center;
                    transition: all 0.3s ease;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }

                .featured-category-card:hover .featured-category-content {
                    border-color: #8abe8eff;
                    box-shadow: 0 4px 12px rgba(150, 8, 93, 0.15);
                    transform: translateY(-5px);
                }

                .featured-category-content img {
                    width: 120px;
                    height: 120px;
                    object-fit: contain;
                    margin-bottom: 8px;
                    transition: transform 0.3s ease;
                }

                .featured-category-card:hover .featured-category-content img {
                    transform: scale(1.1);
                }

                .featured-category-name {
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #2B2D42;
                    transition: color 0.3s ease;
                }

                .featured-category-card:hover .featured-category-name {
                    color: #96085d;
                }

                @media (max-width: 768px) {
                    .featured-categories-section {
                        padding: 20px 15px 0;
                    }

                    .featured-category-card {
                        width: 140px;
                    }

                    .featured-category-content {
                        padding: 10px 7px;
                    }

                    .featured-category-content img {
                        width: 90px;
                        height: 90px;
                    }

                    .featured-category-name {
                        font-size: 1.2rem;
                    }
                }

                @media (max-width: 480px) {
                    .featured-category-card {
                        width: 120px;
                    }

                    .featured-category-content {
                        padding: 10px 6px;
                    }

                    .featured-category-content img {
                        width: 70px;
                        height: 70px;
                    }

                    .featured-category-name {
                        font-size: 1.05rem;
                    }
                }

                .products-home-section {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 10px 20px;
                    background: #ffffff;
                    position: relative;
                }

                .products-section {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 20px 10px;
                    background: #ffffff;
                    position: relative;
                }

                @media (max-width: 768px) {
                    .products-section {
                        max-width: 100%;
                        margin: 0;
                        padding: 0 0 10px;
                    }
                }

                @media (max-width: 480px) {
                    .products-section {
                        max-width: 100%;
                        margin: 0;
                        padding: 0 0 10px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Home3;
